// The demo mode of the camera slide: when there is no camera, or the hand
// model will not load, the instructor drives two drawn hands from the
// keyboard instead, and the slide tells the same story.

export const SIM_START = { left: 2, right: 3, gap: 4.73 };

const GAP_STEP = 0.37;
const KEYS = {
  d: ['demo', 0], // switch the slide to its demo; the stop handles it
  a: ['left', -1],
  s: ['left', 1],
  k: ['right', -1],
  l: ['right', 1],
  ArrowDown: ['gap', -GAP_STEP],
  ArrowUp: ['gap', GAP_STEP],
};

/**
 * Which demo key an event is, or null. Letters are matched case-blind and by
 * physical key too, so the demo still works with the keyboard set to Thai.
 * None of these collide with the deck's own shortcuts.
 */
export function simKey(e) {
  if (e.key === 'ArrowUp' || e.key === 'ArrowDown') return e.key;
  const physical = (/^Key([A-Z])$/.exec(e.code || '') || [])[1];
  const letter = (e.key && e.key.length === 1 ? e.key.toLowerCase() : null);
  if (letter && KEYS[letter]) return letter;
  if (physical && KEYS[physical.toLowerCase()]) return physical.toLowerCase();
  return null;
}

/** The demo state after one key press; the same object back for any other key. */
export function applySimKey(state, key) {
  const k = KEYS[key];
  if (!k || k[0] === 'demo') return state;
  const [field, step] = k;
  if (field === 'gap') {
    const gap = Math.round(Math.min(12, Math.max(0.5, state.gap + step)) * 100) / 100;
    return { ...state, gap };
  }
  return { ...state, [field]: Math.min(5, Math.max(0, state[field] + step)) };
}

/* A drawn hand, as the 21 landmarks MediaPipe would report for it, in image
   coordinates (the screen shows the video mirrored, so screen x = 1 - x).
   Palm to the camera, fingers up; counting raises index, middle, ring,
   pinky, then the thumb. Units are hand-sized; x is squeezed by 9/16 so the
   hand looks right drawn over a 16:9 frame. With `pinchCm`, the thumb and
   index reach toward each other, their tips apart in proportion to it. */
const U = 0.03;
const SQUEEZE = 9 / 16;
const PINCH_UNITS_PER_CM = 0.75;

export function simHandLandmarks({ fingers, side, pinchCm = null }) {
  const screenCx = side === 'left' ? 0.27 : 0.73;
  const cy = 0.86;
  // Thumb points toward the middle of the screen.
  const t = side === 'left' ? 1 : -1;
  const p = (dx, dy) => ({ x: 1 - (screenCx + dx * t * U * SQUEEZE), y: cy + dy * U, z: 0 });

  const thumbUp = fingers >= 5;
  const thumb = thumbUp
    ? [p(1.4, -1.6), p(3, -3), p(5, -4), p(8, -4.5)]
    : [p(1.4, -1.6), p(3, -3), p(2.2, -5), p(0.4, -5.6)];

  const bases = [2.6, 0.9, -0.8, -2.4]; // index, middle, ring, pinky (x)
  const tops = [-8.2, -8.8, -8.4, -7.4];
  const lens = [[3.4, 2.4, 2], [3.8, 2.6, 2.1], [3.4, 2.4, 2], [2.8, 2, 1.7]];
  const fingerPts = bases.flatMap((bx, i) => {
    const up = i < Math.min(fingers, 4);
    const my = tops[i];
    const [a, b, c] = lens[i];
    const spread = (bx - 0.2) * 0.12;
    if (up) {
      return [p(bx, my), p(bx + spread, my - a), p(bx + spread * 2, my - a - b), p(bx + spread * 3, my - a - b - c)];
    }
    return [p(bx, my), p(bx, my - 2.2), p(bx * 0.95, my - 1.4), p(bx * 0.9, my - 0.4)];
  });

  if (pinchCm != null) {
    // Thumb and index curl toward a point beside the hand; their tips sit
    // above and below it, `gap` units apart. Busy pinching, they cannot
    // count, so the raised fingers are middle, ring and pinky — up to 3.
    const raised = Math.min(fingers, 3);
    fingerPts.splice(4, 12, ...bases.slice(1).flatMap((bx, j) => {
      const i = j + 1;
      const my = tops[i];
      const [a, b, c] = lens[i];
      const spread = (bx - 0.2) * 0.12;
      if (j < raised) {
        return [p(bx, my), p(bx + spread, my - a), p(bx + spread * 2, my - a - b), p(bx + spread * 3, my - a - b - c)];
      }
      return [p(bx, my), p(bx, my - 2.2), p(bx * 0.95, my - 1.4), p(bx * 0.9, my - 0.4)];
    }));
    const gap = Math.max(0.4, pinchCm * PINCH_UNITS_PER_CM);
    const mid = -8.6;
    thumb.splice(0, 4, p(1.4, -1.6), p(3.2, -3.2), p(4.6, -5.2), p(5.4, mid + gap / 2));
    fingerPts.splice(0, 4, p(2.6, -8.2), p(4, -10.6), p(5.2, -11.6), p(5.6, mid - gap / 2));
  }

  return [p(0, 0), ...thumb, ...fingerPts];
}
